import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="video-picker"
// The "choose a video, set its link" form for public videos: filters the
// library as you type, previews the chosen video, and suggests a link from
// its title.
export default class extends Controller {
  static targets = [
    "search", "kind", "item", "group", "count", "empty",
    "placeholder", "preview", "previewImage", "previewTitle", "previewMeta",
    "slug", "linkSlug"
  ]

  static values = {
    kind: { type: String, default: "all" },
    // Once a link has been typed (or already exists), choosing a different
    // video must not overwrite it.
    slugLocked: Boolean
  }

  connect() {
    this.filter()
    this.showSelected()
    this.updateLink()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    let visible = 0

    this.itemTargets.forEach(item => {
      const show = (this.kindValue === "all" || item.dataset.kind === this.kindValue) &&
        item.dataset.search.includes(query)
      item.hidden = !show
      if (show) visible++
    })

    this.groupTargets.forEach(group => {
      group.hidden = !this.itemTargets.some(item => !item.hidden && group.contains(item))
    })

    this.countTarget.textContent = `${visible} ${visible === 1 ? "video" : "videos"}`
    this.emptyTarget.hidden = visible > 0
  }

  setKind(event) {
    this.kindValue = event.currentTarget.dataset.kind
    this.kindTargets.forEach(button => {
      button.setAttribute("aria-pressed", button.dataset.kind === this.kindValue)
    })
    this.filter()
  }

  select() {
    this.showSelected()

    const item = this.selectedItem
    if (item && !this.slugLockedValue) this.slugTarget.value = this.slugify(item.dataset.title)
    this.updateLink()
  }

  slugTyped() {
    // Clearing the box hands the suggestion back to the chosen video.
    this.slugLockedValue = this.slugTarget.value.trim() !== ""
    this.updateLink()
  }

  showSelected() {
    const item = this.selectedItem
    this.placeholderTarget.hidden = !!item
    this.previewTarget.hidden = !item
    if (!item) return

    this.previewTitleTarget.textContent = item.dataset.title
    this.previewMetaTarget.textContent = item.dataset.meta
    this.previewImageTarget.hidden = !item.dataset.thumbnail
    if (item.dataset.thumbnail) this.previewImageTarget.src = item.dataset.thumbnail
  }

  updateLink() {
    const slug = this.slugify(this.slugTarget.value)
    this.linkSlugTarget.textContent = slug || "your-link"
    this.linkSlugTarget.classList.toggle("opacity-50", !slug)
  }

  get selectedItem() {
    return this.itemTargets.find(item => item.querySelector("input[type=radio]").checked)
  }

  // Mirrors how the server tidies the link: lower case, with anything that is
  // not a letter or number turned into a dash.
  slugify(text) {
    return text.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-").replace(/-{2,}/g, "-").replace(/^-|-$/g, "")
  }
}
